import moment from 'moment';
import GeneralEnumService from '../../enumerables/general-enum';
import { getValFromEnum } from '../../components/redux-form/utils/dropdown-values';

type GroupedTarget = {
    target: any;
    shortTerm: any;
    longTerm: any;
    domain: any;
    section: any;
    isClickedAnsweredEmpty: boolean;
    answers: any[];
    phases: any[];
    events?: any[];
    formatAnswers: any[];
};

export const groupProgramAnswerForPlotV4 = (
    data: any,
    answersByTargetId: Record<number, any[]>,
    globalFilters: any,
    isHideButton: any,
    alreadyProcessed: Record<number, GroupedTarget> = {},
): {
    groupedTargets: Record<number, GroupedTarget>;
    tree: any[];
} => {

    const { programMainCategories, ...res } = data;

    const EnumSet = GeneralEnumService ? GeneralEnumService.getEnum('ProgramPrompt') : {};
    const programPromptItems: SelectObject[] = getValFromEnum(EnumSet);

    const groupedTargets: Record<number, GroupedTarget> = { ...alreadyProcessed };
    const dataSectionSet: any[] = [];

    if (programMainCategories) {
        let sectionInfo: any = {};

        Object.values(programMainCategories).forEach((section: any, i: number) => {
            sectionInfo = {
                id: section.id,
                title: section.title,
                isHidden: section.isHidden,
                description: section.description,
            };

            const dataDomainSet: any[] = [];

            section.programCategories = section.programCategories
                ? !Array.isArray(section.programCategories)
                    ? Object.values(section.programCategories)
                    : section.programCategories
                : [];

            section.programCategories.forEach((domain: any, j: number) => {
                const domainInfo = {
                    id: domain.id,
                    title: domain.title,
                    isHidden: domain.isHidden,
                    description: domain.description,
                };

                const dataLongTermSet: any[] = [];

                domain.programCategories = domain.programCategories
                    ? !Array.isArray(domain.programCategories)
                        ? Object.values(domain.programCategories)
                        : domain.programCategories
                    : [];

                domain.programCategories.forEach((longTerm: any, k: number) => {
                    const longTermInfo = {
                        id: longTerm.id,
                        title: longTerm.title,
                        isHidden: longTerm.isHidden,
                        description: longTerm.description,
                    };

                    const dataShortTermSet: any[] = [];

                    longTerm.programCategories = longTerm.programCategories
                        ? !Array.isArray(longTerm.programCategories)
                            ? Object.values(longTerm.programCategories)
                            : longTerm.programCategories
                        : [];

                    longTerm.programCategories.forEach((shortTerm: any, l: number) => {
                        const shortTermInfo = {
                            id: shortTerm.id,
                            title: shortTerm.title,
                            isHidden: shortTerm.isHidden,
                            description: shortTerm.description,
                        };

                        const shortTermTargets: GroupedTarget[] = [];

                        shortTerm.programTargets = shortTerm.programTargets
                            ? !Array.isArray(shortTerm.programTargets)
                                ? Object.values(shortTerm.programTargets)
                                : shortTerm.programTargets
                            : [];

                        shortTerm.programTargets.forEach((target: any) => {
                            const rawAnswers = answersByTargetId[target.id] ?? [];

                            // 🔹 1. no answers for this target yet and the button has not been pressed —
                            //     just create a placeholder (so the "See results" button renders)
                            if (!rawAnswers.length && !groupedTargets[target.id]) {
                                shortTermTargets.push({
                                    target,
                                    shortTerm: shortTermInfo,
                                    longTerm: longTermInfo,
                                    domain: domainInfo,
                                    section: sectionInfo,
                                    isClickedAnsweredEmpty: false,
                                    answers: [],
                                    phases: [],
                                    events: [],
                                    formatAnswers: [],
                                });
                                return;
                            }

                            // 🔹 2. target already computed and the raw answer count has NOT changed —
                            //     reuse the existing result
                            const cached = groupedTargets[target.id];
                            if (cached && cached.answers.length === rawAnswers.length) {
                                shortTermTargets.push(cached);
                                return;
                            }

                            // 🔹 3. otherwise — recompute ONLY this target
                            const valPrompt = programPromptItems.find((it) => it.value === target.prompt);
                            const targetInfo = {
                                id: target.id,
                                title: target.title,
                                prompt: valPrompt ? valPrompt.label : '',
                                type: target.type,
                                description: target.description,
                                isHidden: target.isHidden,
                                programTargetPhases: target.programTargetPhases,
                                isPhased: target.isPhased,
                            };

                            let answersSet = [...rawAnswers];

                            const prompts: any[] = [];
                            if (target.type == 1) {
                                const promptValue = programPromptItems.find(
                                    (it: SelectObject) => it.value == target.prompt,
                                );
                                if (promptValue) prompts.push(promptValue.label);
                            } else if (target.type == 3) {
                                target.programTargetOptions.forEach((p: any) => {
                                    const promptValue = programPromptItems.find(
                                        (it: SelectObject) => it.value == p.prompt,
                                    );
                                    if (promptValue) {
                                        prompts.push(promptValue.label);
                                        targetInfo.prompt = prompts.join(',');
                                    }
                                });
                            }

                            const usedKeys = new Set<string>();



                            answersSet = answersSet
                                .map((answer: any) => {

                                    const seanceDate = answer.seance
                                        ? moment(answer.seance.startTime, 'YYYY-MM-DD HH:mm:ss').format('ll')
                                        : '[canceled]';

                                    const key = [
                                        seanceDate,
                                        answer.answer,
                                        target.isHidden,
                                        answer.isSkip,
                                        prompts.join(','),
                                    ].join('|');

                                    if (usedKeys.has(key)) {
                                        return null;
                                    }
                                    usedKeys.add(key);

                                    const programTargetPhases = Array.isArray(target?.programTargetPhases)
                                        ? target.programTargetPhases
                                        : target?.programTargetPhases
                                        ? Object.values(target.programTargetPhases)
                                        : [];

                                    const activePhase = programTargetPhases.find(
                                        (its: any) =>
                                            (moment(answer.seance.startTime, 'YYYY-MM-DD').isSameOrAfter(
                                                moment(its.startDate, 'YYYY-MM-DD'),
                                            ) &&
                                                moment(answer.seance.startTime, 'YYYY-MM-DD').isSameOrBefore(
                                                    moment(its.endDate, 'YYYY-MM-DD'),
                                                )) ||
                                            (moment(answer.seance.startTime, 'YYYY-MM-DD').isSameOrAfter(
                                                moment(its.startDate, 'YYYY-MM-DD'),
                                            ) &&
                                                its.endDate == null),
                                    );

                                    return {
                                        answer: answer.answer,
                                        isHidden: target.isHidden,
                                        phasePeriod: false,
                                        phasePeriodSortFlag: 1,
                                        phaseTitle: '',
                                        isSkip: answer.isSkip,
                                        prompts: prompts.join(','),
                                        activePhase: target.isPhased ? activePhase : null,
                                        seanceDate,
                                        event: answer.seance?.eventInfo ?? null,
                                    };
                                })
                                .filter(Boolean) as any[];

                            const phases: any[] = [];

                            if (target.isPhased && target.programTargetPhases?.length && answersSet.length) {
                                target.programTargetPhases.forEach((phase: any, m: number) => {
                                    if (phase.startDate) {
                                        answersSet.push({
                                            answer: 0,
                                            isHidden: false,
                                            isSkip: false,
                                            prompts: '',
                                            event: null,
                                            activePhase: null,
                                            phasePeriod: true,
                                            phasePeriodSortFlag: 0,
                                            phaseTitle: 'Phase ' + phase.title + ' starts.',
                                            seanceDate: moment(phase.startDate, 'YYYY-MM-DD HH:mm:ss').format('ll'),
                                        });
                                    }
                                    if (phase.endDate) {
                                        answersSet.push({
                                            answer: 0,
                                            isHidden: false,
                                            isSkip: false,
                                            prompts: '',
                                            event: null,
                                            activePhase: null,
                                            phasePeriod: true,
                                            phasePeriodSortFlag: 2,
                                            phaseTitle: 'Phase ' + phase.title + ' ends.',
                                            seanceDate: moment(phase.endDate, 'YYYY-MM-DD HH:mm:ss').format('ll'),
                                        });
                                    }

                                    const startDate =
                                        phase.startDate &&
                                        phase.startDate > globalFilters.dateFrom &&
                                        phase.startDate < globalFilters.dateTo
                                            ? moment(phase.startDate, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD')
                                            : null;
                                    const endDate =
                                        phase.endDate &&
                                        phase.endDate > globalFilters.dateFrom &&
                                        phase.endDate < globalFilters.dateTo
                                            ? moment(phase.endDate, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD')
                                            : null;
                                    const phaseTitle = phase.title ? phase.title : 'No Phase';
                                    let uniqueTitle = phaseTitle + ': ' + startDate;
                                    if (endDate) uniqueTitle += ' - ' + endDate;

                                    phases.push({
                                        id: m,
                                        name: uniqueTitle,
                                        start: startDate,
                                        end: endDate,
                                    });
                                });
                            }

                            answersSet = answersSet.filter((el: any) => el.isSkip != true);

                            answersSet.sort((a: any, b: any) => {
                                const c: any = new Date(a.seanceDate);
                                const d: any = new Date(b.seanceDate);
                                if (a.seanceDate == b.seanceDate) {
                                    return a.phasePeriodSortFlag - b.phasePeriodSortFlag;
                                }
                                return c - d;
                            });

                            let isClickedButEmpty = false;
                            if (!answersSet.length && isHideButton && isHideButton.indexOf(target.id) > -1) {
                                isClickedButEmpty = true;
                            }

                            const formatAnswers: any[] = [];
                            const events: any[] = [];
                            const answersSetClean = answersSet.filter((el: any) => !el.phasePeriod);

                            answersSetClean.forEach((ans: any) => {
                                let uniqueTitle = '';
                                if (ans?.activePhase?.title) {
                                    const startDate =
                                        ans.activePhase?.startDate &&
                                        ans.activePhase?.startDate > globalFilters.dateFrom &&
                                        ans.activePhase?.startDate < globalFilters.dateTo
                                            ? moment(ans.activePhase?.startDate, 'YYYY-MM-DD HH:mm:ss').format(
                                                  'YYYY-MM-DD',
                                              )
                                            : null;
                                    const endDate =
                                        ans.activePhase?.endDate &&
                                        ans.activePhase?.endDate > globalFilters.dateFrom &&
                                        ans.activePhase?.endDate < globalFilters.dateTo
                                            ? moment(ans.activePhase?.endDate, 'YYYY-MM-DD HH:mm:ss').format(
                                                  'YYYY-MM-DD',
                                              )
                                            : null;
                                    const phaseTitle = ans.activePhase?.title ? ans.activePhase?.title : 'No Phase';
                                    uniqueTitle = phaseTitle + ': ' + startDate;
                                    if (endDate) uniqueTitle += ' - ' + endDate;
                                }

                                formatAnswers.push({
                                    date: moment(ans.seanceDate, 'll').format('YYYY-MM-DD'),
                                    series: uniqueTitle ? uniqueTitle : targetInfo.title,
                                    value: ans?.answer,
                                });
                                if (ans?.event) {
                                    events.push({
                                        date: moment(ans.seanceDate, 'll').format('YYYY-MM-DD'),
                                        title: ans?.event,
                                    });
                                }
                            });

                            if (!phases.length) {
                                phases.push({
                                    id: 0,
                                    name: targetInfo.title,
                                    start: answersSet.length ? answersSet[0].seanceDate : '',
                                    end: null,
                                });
                            }

                            const grouped: GroupedTarget = {
                                target: targetInfo,
                                shortTerm: shortTermInfo,
                                longTerm: longTermInfo,
                                domain: domainInfo,
                                section: sectionInfo,
                                isClickedAnsweredEmpty: isClickedButEmpty,
                                answers: answersSet,
                                phases,
                                events,
                                formatAnswers,
                            };

                            groupedTargets[target.id] = grouped;
                            shortTermTargets.push(grouped);
                        });

                        dataShortTermSet[l] = {
                            shortTerm: shortTermInfo,
                            answers: shortTermTargets,
                        };
                    });

                    dataLongTermSet[k] = {
                        longTerm: longTermInfo,
                        dataShortTerm: dataShortTermSet,
                    };
                });

                dataDomainSet[j] = {
                    domain: domainInfo,
                    dataLongTerm: dataLongTermSet,
                };
            });

            dataSectionSet[i] = {
                section: sectionInfo,
                dataDomain: dataDomainSet,
            };
        });
    }

    return {
        groupedTargets,
        tree: dataSectionSet,
    };
};
